/**
 * Opening and structurally upgrading the record database.
 *
 * The IndexedDB database version is used for **structural** change only —
 * creating an object store, adding an index. It never transforms record
 * content. Record migration is a separate, explicit, resumable pass driven by
 * the version *inside* each envelope (`recordStore.migrate`), because a
 * structural upgrade runs once per browser profile and cannot be resumed, while
 * a content migration has to survive being interrupted halfway through a
 * hundred thousand rows.
 *
 * The upgrade is therefore **additive only**: it creates what is missing and
 * touches nothing that exists. A `deleteObjectStore` here would destroy user
 * records during what the user experiences as an app update, which is precisely
 * the failure the fail-closed contract exists to prevent — and unlike a refused
 * read, it is not recoverable afterwards.
 */

import { openDB } from "idb";
import type { IDBPDatabase, IDBPTransaction } from "idb";

import type { RecordStoreLayout } from "./layout";
import { RECORD_STORE_LAYOUTS } from "./layout";

export const RECORD_DATABASE_NAME = "fighting-form";

/**
 * Bumped only when {@link RECORD_STORE_LAYOUTS} gains a store or an index.
 * Payload shape changes do not touch it — they travel in the envelope.
 */
export const RECORD_DATABASE_VERSION = 1;

export interface RecordDatabaseOptions {
  readonly name?: string;
  readonly version?: number;
  readonly layouts?: readonly RecordStoreLayout[];
}

/**
 * A lazily opened, shared connection.
 *
 * Every store in a transaction has to come from the *same* connection or the
 * transaction cannot span them — and spanning stores is the whole reason this
 * package is on IndexedDB (a journal entry, its draft and its attachment
 * cleanup commit together or not at all). So the connection is a handle passed
 * to each store, not something a store opens for itself.
 */
export interface RecordDatabase {
  readonly name: string;
  connection(): Promise<IDBPDatabase>;
  close(): void;
}

export function recordDatabase(options: RecordDatabaseOptions = {}): RecordDatabase {
  const name = options.name ?? RECORD_DATABASE_NAME;
  const version = options.version ?? RECORD_DATABASE_VERSION;
  const layouts = options.layouts ?? RECORD_STORE_LAYOUTS;

  let pending: Promise<IDBPDatabase> | undefined;

  const openConnection = (): Promise<IDBPDatabase> => {
    const opening = openDB(name, version, {
      upgrade(database, _oldVersion, _newVersion, transaction) {
        upgradeRecordDatabase(database, transaction, layouts);
      },
      blocking() {
        // Another tab is trying to upgrade and this connection is what is
        // holding it back. Closing lets the upgrade proceed; the next call to
        // `connection()` reopens at the new version. Holding on instead would
        // strand the other tab indefinitely, and the user has no way to know
        // which tab to close.
        forget();
      },
      terminated() {
        // The browser dropped the connection (storage pressure, profile
        // eviction). Forgetting it means the next read reopens rather than
        // resolving forever against a dead handle.
        forget();
      },
    });

    return opening.then(
      (database) => {
        database.addEventListener("close", forget);
        return database;
      },
      (error: unknown) => {
        // A failed open must not be cached, or one transient failure would make
        // the store permanently unreadable for the life of the tab.
        pending = undefined;
        throw error;
      },
    );
  };

  const forget = (): void => {
    const outstanding = pending;
    pending = undefined;
    void outstanding?.then(
      (database) => {
        database.close();
      },
      () => {
        // Already rejected; nothing to close.
      },
    );
  };

  return {
    name,
    connection(): Promise<IDBPDatabase> {
      pending ??= openConnection();
      return pending;
    },
    close(): void {
      forget();
    },
  };
}

/**
 * Creates every store and index in `layouts` that the database does not already
 * have. Exported so the upgrade can be exercised directly against a database
 * opened at an older layout — the only way to prove that a version bump
 * migrates rather than drops.
 */
export function upgradeRecordDatabase(
  database: IDBPDatabase,
  transaction: IDBPTransaction<unknown, string[], "versionchange">,
  layouts: readonly RecordStoreLayout[] = RECORD_STORE_LAYOUTS,
): void {
  for (const layout of layouts) {
    const name = layout.store as string;
    const existed = database.objectStoreNames.contains(name);

    if (!existed) {
      if (layout.keying.kind === "inline") {
        database.createObjectStore(name, { keyPath: layout.keying.keyPath });
      } else {
        // No `keyPath`: the key is supplied explicitly on every write and is
        // always the same literal, so a second record has no address.
        database.createObjectStore(name);
      }
    }

    const store = transaction.objectStore(name);
    for (const descriptor of layout.indexes) {
      if (store.indexNames.contains(descriptor.name)) continue;
      store.createIndex(descriptor.name, descriptor.keyPath, {
        // Deliberately never `unique` — see the layout file: duplicate identity
        // is a read-time refusal the user is told about, not a write-time
        // exception a caller can swallow.
        multiEntry: descriptor.multiEntry,
      });
    }
  }
}
